import { omit } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  createSelectedRowKey,
  objectContainsEvery
} from "../../modules/samples/util";

export type SelectRows<T> = {
  isSelectAll: boolean;
  selected: Record<string, T>;
  api: {
    selectPage: (data: Array<T>, page: number) => void;
    selectAll: (data: Array<T>, page: number) => void;
    checkIsSelected: (item: T, index: number, page: number) => boolean;
    select: (item: T, index: number, page: number) => void;
    isPageSelected: (data: Array<T>, page: number) => boolean;
    clear: () => void;
  };
};

const useSelectRows = <T>(): SelectRows<T> => {
  const [selected, setSelected] = useState<Record<string, T>>({});
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const createSelectedRows = useCallback(
    (data: Array<T>, page: number) =>
      data.reduce(
        (object, item, index) => ({
          ...object,
          ...{ [createSelectedRowKey(index, page, item)]: item }
        }),
        {}
      ),
    []
  );

  const toggleSelectedRows = useCallback(
    (object = {}, selectAll = false) => {
      if (objectContainsEvery(object, selected) && !selectAll) {
        setSelected(prevState => omit(prevState, Object.keys(object)));
      } else {
        setSelected(prevState => ({
          ...prevState,
          ...object
        }));
      }
      setIsSelectAll(selectAll);
    },
    [selected]
  );

  const api = useMemo(() => {
    const select = (item: T, index: number, page: number) => {
      const rowKey = createSelectedRowKey(index, page, item);
      toggleSelectedRows({ [rowKey]: item });
    };

    const selectPage = (data: Array<T>, page: number) => {
      const selectedRows = createSelectedRows(data, page);
      toggleSelectedRows(selectedRows, false);
    };

    const selectAll = (data: Array<T>, page: number) => {
      const selectedRows = createSelectedRows(data, page);
      toggleSelectedRows(selectedRows, true);
    };

    const checkIsSelected = (item: T, index: number, page: number) => {
      const rowKey = createSelectedRowKey(index, page, item);
      return Boolean(selected[rowKey]);
    };

    const isPageSelected = (data: Array<T>, page: number) =>
      Boolean(data.length) &&
      data.every((item, index) => {
        const key = createSelectedRowKey(index, page, item);
        return Boolean(selected[key]);
      });

    const clear = () => {
      setIsSelectAll(false);
      setSelected({});
    };

    return {
      selectPage,
      selectAll,
      checkIsSelected,
      select,
      isPageSelected,
      clear
    };
  }, [toggleSelectedRows, createSelectedRows, selected]);

  return {
    selected,
    isSelectAll,
    api
  };
};

export default useSelectRows;
